<script setup lang="ts">
import { ImageMedia } from '@clovyr/bed';
import alertHighIcon from '@clovyr/bed/assets/images/alert-high-icon.svg';
import recoveryKitDirections from '@clovyr/bed/assets/images/recovery-kit-directions.png';
import recoveryKitScreenshot from '@clovyr/bed/assets/images/recovery-kit-screenshot.png';
import secretKeyScreenshot from '@clovyr/bed/assets/images/secret-key-screenshot.png';

import HelpPage from './HelpPage.vue';
</script>

<!-- Commenting this out for now, until needed -->
<!-- :previousPage="{
  page: 'recovery-kit-help',
  name: 'Clovyr Recovery Kit Help',
}" -->

<template>
  <HelpPage @navigate="(page) => $emit('navigate', page)">
    <div class="recovery-kit-help">
      <div class="section">
        <div class="section-headline">Everything about your Recovery Kit</div>
        <div class="section-text">
          Your Recovery Kit contains your Secret Key that you will need to sign in if you get locked
          out of your account. Your secret key with your Clovyr account password protects your data
          and your apps.
        </div>
        <div class="key-warning">
          <ImageMedia :filename="alertHighIcon" />
          <p>
            Clovyr does not have access to your Secret Key and cannot help recover it if you lose
            it.
          </p>
        </div>
      </div>
      <div class="section">
        <div class="section-title">What is the secret key</div>
        <div class="section-text">
          Your Secret Key is 34 letters and numbers that protects your account by adding another
          level of security on top of your account password. It’s stored in devices you’ve used to
          sign in with, and in your Recovery Kit.
        </div>
        <div class="secret-key">
          <ImageMedia :filename="secretKeyScreenshot" />
        </div>
      </div>
      <div class="section">
        <div class="section-title">Where to find your Recovery Kit</div>
        <div class="section-text">
          You are prompted to save your Recovery Kit when you create an account. Try looking in your
          Downloads folder for a PDF file name “Clovyr Recovery Kit.”
        </div>
        <div class="section-text">
          You can redownload your Recovery Kit or get a copy of your Secret Key by logging into your
          Clovyr Account on the Settings page.
        </div>
        <ImageMedia :filename="recoveryKitDirections" />
        <div class="section-text">
          You can regenerate your Secret Key if you happened to have lost access to a device. Be
          sure to save another copy of the Recovery Kit.
        </div>
      </div>
      <div class="section">
        <div class="section-title">What to do with your Recovery kit</div>
        <div class="section-text">
          No one, not even Clovyr has access to your data without your Secret Key. Make sure to save
          your Recovery Kit in a safe place that only you will have accesss to.
        </div>
        <ImageMedia :filename="recoveryKitScreenshot" />
        <div class="section-text">
          <ol>
            <li class="list-item">Fill out your account password</li>
            <li class="list-item">
              Print or download this page and store somewhere safe (USB key or external drive).
              Store your kit in secure place where you can find it, e.g. fireproof box
            </li>
            <li class="list-item">
              To restore data or add new devices, visit
              <span class="clovyr-green">clovyr.app/settings</span>
            </li>
          </ol>
        </div>
      </div>
    </div>
  </HelpPage>
</template>

<style scoped lang="scss">
@use '@clovyr/bed/styles/vendor/bootstrap/scss/bootstrap';
@use '@clovyr/bed/styles/core';
@use 'src/styles/core/page';
@use 'src/styles/core/vertical-spacing';
@use 'styles/core/layout/grid-vars';
@use 'styles/vendor/bootstrap/scss/mixins/breakpoints';

.recovery-kit-help {
  display: flex;
  flex-direction: column;
  justify-content: center;
  background-color: #221a33b3;
  padding: grid-vars.space(2) grid-vars.space(4) 0;

  @include breakpoints.media-breakpoint-up(md) {
    padding: 30px 75px 0;
  }

  .section {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin: 40px 0;

    .section-headline {
      font-size: 24px;
      line-height: 32px;
      letter-spacing: -0.03em;
      color: #ffffff;
    }

    .section-title {
      font-size: 19px;
      line-height: 24px;
      color: #ffffff;
      margin: 0;
    }
    .section-text {
      font-size: 14px;
      line-height: 24px;
      letter-spacing: 0em;
      color: #ada6b7;
      margin: 10px 0;
      padding: 0 15px;
    }

    .list-item {
      margin: 5px 0;
    }
    .key-warning {
      display: flex;
      align-items: center;
      border: 2px solid #ff957d80;
      border-radius: 8px;
      background-color: #ff957d12;
      color: #ff957d;
      margin: 10px 0 0 0;
      padding: 5px 10px 5px 5px;
      .image-media {
        padding: 5px 10px 5px 5px;
      }
    }
    .secret-key {
      width: 110%;
    }
    .image-media {
      padding: 20px 0;
    }
  }
}
</style>
